import React, { useState } from 'react';
import {
  BookOpen, Search, Filter, Grid3X3, List, Clock,
  Star, Download, Eye, Heart, Share2, Bookmark,
  Tag, User, Calendar, FileText, Video, Headphones,
  Image, Code, Database, Brain, Layers, Trophy,
  TrendingUp, Award, Play, Plus, ChevronRight,
  ExternalLink, Folder, Archive, RefreshCw, Upload
} from 'lucide-react';

interface Resource {
  id: string;
  title: string;
  description: string;
  type: 'article' | 'video' | 'ebook' | 'podcast' | 'tool' | 'template' | 'code' | 'dataset';
  category: string;
  author: {
    name: string;
    avatar: string;
    verified: boolean;
  };
  publishedDate: string;
  readTime?: number;
  duration?: number;
  rating: number;
  downloads: number;
  views: number;
  tags: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  isPremium: boolean;
  isBookmarked: boolean;
  url?: string;
  thumbnail: string;
  format?: string;
  size?: string;
}

interface Collection {
  id: string;
  name: string;
  description: string;
  resourceCount: number;
  isPublic: boolean;
  thumbnail: string;
  lastUpdated: string;
}

export default function Library() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'popular' | 'rating' | 'downloads'>('recent');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedResource, setSelectedResource] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'resources' | 'collections' | 'uploaded'>('resources');

  const categories = [
    { id: 'all', name: 'All Categories', count: 1240 },
    { id: 'programming', name: 'Programming', count: 456 },
    { id: 'design', name: 'Design', count: 234 },
    { id: 'data-science', name: 'Data Science', count: 189 },
    { id: 'business', name: 'Business', count: 167 },
    { id: 'ai-ml', name: 'AI & ML', count: 134 },
    { id: 'security', name: 'Security', count: 89 },
    { id: 'devops', name: 'DevOps', count: 78 },
    { id: 'mobile', name: 'Mobile Dev', count: 67 }
  ];

  const resourceTypes = [
    { id: 'all', name: 'All Types', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'article', name: 'Articles', icon: <FileText className="w-4 h-4" /> },
    { id: 'video', name: 'Videos', icon: <Video className="w-4 h-4" /> },
    { id: 'ebook', name: 'E-books', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'podcast', name: 'Podcasts', icon: <Headphones className="w-4 h-4" /> },
    { id: 'tool', name: 'Tools', icon: <Code className="w-4 h-4" /> },
    { id: 'template', name: 'Templates', icon: <Layers className="w-4 h-4" /> },
    { id: 'dataset', name: 'Datasets', icon: <Database className="w-4 h-4" /> }
  ];

  const resources: Resource[] = [
    {
      id: '1',
      title: 'Complete React.js Masterclass 2024',
      description: 'Comprehensive guide covering React fundamentals, hooks, context, and advanced patterns',
      type: 'video',
      category: 'programming',
      author: {
        name: 'Sarah Chen',
        avatar: '👩‍💻',
        verified: true
      },
      publishedDate: '2024-01-28',
      duration: 480, // 8 hours
      rating: 4.9,
      downloads: 2340,
      views: 15420,
      tags: ['React', 'JavaScript', 'Frontend', 'Components', 'Hooks'],
      difficulty: 'intermediate',
      isPremium: true,
      isBookmarked: true,
      thumbnail: '/api/placeholder/400/225',
      format: 'MP4',
      size: '2.4 GB'
    },
    {
      id: '2',
      title: 'Machine Learning Algorithms Cheat Sheet',
      description: 'Visual guide to ML algorithms with implementation examples in Python',
      type: 'ebook',
      category: 'ai-ml',
      author: {
        name: 'Dr. Alex Kumar',
        avatar: '👨‍🔬',
        verified: true
      },
      publishedDate: '2024-01-25',
      readTime: 45,
      rating: 4.7,
      downloads: 5670,
      views: 8920,
      tags: ['Machine Learning', 'Python', 'Algorithms', 'Data Science'],
      difficulty: 'advanced',
      isPremium: false,
      isBookmarked: false,
      thumbnail: '/api/placeholder/400/225',
      format: 'PDF',
      size: '15.2 MB'
    },
    {
      id: '3',
      title: 'Figma Design System Template',
      description: 'Complete design system template with components, tokens, and documentation',
      type: 'template',
      category: 'design',
      author: {
        name: 'Emma Rodriguez',
        avatar: '🎨',
        verified: true
      },
      publishedDate: '2024-01-30',
      rating: 4.8,
      downloads: 1890,
      views: 4560,
      tags: ['Figma', 'Design System', 'UI Kit', 'Components'],
      difficulty: 'intermediate',
      isPremium: true,
      isBookmarked: true,
      thumbnail: '/api/placeholder/400/225',
      format: 'Figma File',
      size: '24.7 MB'
    },
    {
      id: '4',
      title: 'Docker for Developers Podcast Series',
      description: '10-episode series covering containerization, orchestration, and deployment strategies',
      type: 'podcast',
      category: 'devops',
      author: {
        name: 'DevOps Weekly',
        avatar: '🚀',
        verified: true
      },
      publishedDate: '2024-01-20',
      duration: 300, // 5 hours total
      rating: 4.6,
      downloads: 3450,
      views: 7890,
      tags: ['Docker', 'DevOps', 'Containers', 'Deployment'],
      difficulty: 'intermediate',
      isPremium: false,
      isBookmarked: false,
      thumbnail: '/api/placeholder/400/225',
      format: 'MP3',
      size: '456 MB'
    },
    {
      id: '5',
      title: 'Customer Analytics Dataset',
      description: 'Comprehensive dataset with customer behavior, demographics, and purchase patterns',
      type: 'dataset',
      category: 'data-science',
      author: {
        name: 'DataLab Team',
        avatar: '📊',
        verified: true
      },
      publishedDate: '2024-01-15',
      rating: 4.5,
      downloads: 890,
      views: 2340,
      tags: ['Analytics', 'Customer Data', 'CSV', 'Business Intelligence'],
      difficulty: 'intermediate',
      isPremium: false,
      isBookmarked: true,
      thumbnail: '/api/placeholder/400/225',
      format: 'CSV/JSON',
      size: '127 MB'
    },
    {
      id: '6',
      title: 'Building REST APIs with Node.js',
      description: 'Step-by-step guide to creating scalable REST APIs using Node.js and Express',
      type: 'article',
      category: 'programming',
      author: {
        name: 'Michael Park',
        avatar: '⚡',
        verified: false
      },
      publishedDate: '2024-01-22',
      readTime: 25,
      rating: 4.4,
      downloads: 1240,
      views: 5680,
      tags: ['Node.js', 'REST API', 'Express', 'Backend'],
      difficulty: 'beginner',
      isPremium: false,
      isBookmarked: false,
      thumbnail: '/api/placeholder/400/225'
    }
  ];

  const collections: Collection[] = [
    {
      id: '1',
      name: 'Web Development Essentials',
      description: 'Curated resources for full-stack web development',
      resourceCount: 24,
      isPublic: true,
      thumbnail: '/api/placeholder/300/200',
      lastUpdated: '2024-01-28'
    },
    {
      id: '2',
      name: 'Data Science Toolkit',
      description: 'Complete collection for data science learning',
      resourceCount: 18,
      isPublic: false,
      thumbnail: '/api/placeholder/300/200',
      lastUpdated: '2024-01-25'
    },
    {
      id: '3',
      name: 'Design Inspiration',
      description: 'UI/UX design resources and inspiration',
      resourceCount: 31,
      isPublic: true,
      thumbnail: '/api/placeholder/300/200',
      lastUpdated: '2024-01-30'
    }
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'article': return <FileText className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'ebook': return <BookOpen className="w-4 h-4" />;
      case 'podcast': return <Headphones className="w-4 h-4" />;
      case 'tool': return <Code className="w-4 h-4" />;
      case 'template': return <Layers className="w-4 h-4" />;
      case 'dataset': return <Database className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesType = selectedType === 'all' || resource.type === selectedType;
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesType && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'recent': return new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime();
      case 'popular': return b.views - a.views;
      case 'rating': return b.rating - a.rating;
      case 'downloads': return b.downloads - a.downloads;
      default: return 0;
    }
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-blue-600 to-brand-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <BookOpen className="w-7 h-7" />
              Resource Library
            </h1>
            <p className="text-blue-100">
              Discover curated learning resources, tools, and templates
            </p>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
              <Upload className="w-4 h-4" />
              Upload Resource
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium">
              <Plus className="w-4 h-4" />
              New Collection
            </button>
          </div>
        </div>

        {/* Library Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <BookOpen className="w-6 h-6 mx-auto mb-2 text-blue-200" />
            <p className="text-2xl font-bold">1,240</p>
            <p className="text-xs text-blue-100">Total Resources</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Download className="w-6 h-6 mx-auto mb-2 text-blue-200" />
            <p className="text-2xl font-bold">45.2K</p>
            <p className="text-xs text-blue-100">Downloads</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Folder className="w-6 h-6 mx-auto mb-2 text-blue-200" />
            <p className="text-2xl font-bold">23</p>
            <p className="text-xs text-blue-100">Collections</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Bookmark className="w-6 h-6 mx-auto mb-2 text-blue-200" />
            <p className="text-2xl font-bold">89</p>
            <p className="text-xs text-blue-100">Bookmarked</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="flex border-b border-gray-200">
          {[
            { id: 'resources', label: 'All Resources', count: 1240 },
            { id: 'collections', label: 'Collections', count: 23 },
            { id: 'uploaded', label: 'My Uploads', count: 12 }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-6 py-4 font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-brand-blue-500 text-brand-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <span>{tab.label}</span>
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {activeTab === 'resources' && (
          <div className="p-6">
            {/* Filters and Search */}
            <div className="flex flex-col lg:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search resources, authors, or topics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue-500"
                />
              </div>
              <div className="flex gap-3">
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue-500"
                >
                  {resourceTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue-500"
                >
                  <option value="recent">Most Recent</option>
                  <option value="popular">Most Popular</option>
                  <option value="rating">Highest Rated</option>
                  <option value="downloads">Most Downloaded</option>
                </select>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`p-2 rounded-lg border ${showFilters ? 'bg-blue-50 border-blue-300' : 'border-gray-200'}`}
                >
                  <Filter className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Categories Filter */}
            {showFilters && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Categories</h4>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                        selectedCategory === category.id
                          ? 'border-brand-blue-500 bg-brand-blue-50 text-brand-blue-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span>{category.name}</span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        {category.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Resources Grid/List */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map((resource) => (
                  <div key={resource.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all">
                    <div className="relative">
                      <img
                        src={resource.thumbnail}
                        alt={resource.title}
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                          resource.type === 'video' ? 'bg-red-100 text-red-700' :
                          resource.type === 'article' ? 'bg-blue-100 text-blue-700' :
                          resource.type === 'ebook' ? 'bg-green-100 text-green-700' :
                          resource.type === 'podcast' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {getTypeIcon(resource.type)}
                          {resource.type}
                        </span>
                      </div>
                      {resource.isPremium && (
                        <div className="absolute top-3 right-3">
                          <Trophy className="w-5 h-5 text-yellow-500" />
                        </div>
                      )}
                      <button
                        onClick={() => setSelectedResource(resource.id)}
                        className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all flex items-center justify-center"
                      >
                        <Play className="w-12 h-12 text-white opacity-0 hover:opacity-100 transition-opacity" />
                      </button>
                    </div>
                    
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900 line-clamp-2 flex-1">{resource.title}</h3>
                        <button className={`p-1 rounded ${resource.isBookmarked ? 'text-blue-600' : 'text-gray-400'}`}>
                          <Bookmark className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">{resource.description}</p>
                      
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">{resource.author.avatar}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-gray-700">{resource.author.name}</span>
                          {resource.author.verified && <Award className="w-3 h-3 text-blue-500" />}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500" />
                          <span>{resource.rating}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Download className="w-4 h-4" />
                          <span>{resource.downloads.toLocaleString()}</span>
                        </div>
                        {resource.duration && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{formatDuration(resource.duration)}</span>
                          </div>
                        )}
                        {resource.readTime && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{resource.readTime}m read</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-1 mb-3">
                        {resource.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                            {tag}
                          </span>
                        ))}
                        {resource.tags.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                            +{resource.tags.length - 3}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(resource.difficulty)}`}>
                          {resource.difficulty}
                        </span>
                        <div className="flex gap-2">
                          <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                            <Share2 className="w-4 h-4" />
                          </button>
                          <button className="flex items-center gap-2 px-3 py-1.5 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 text-sm">
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* List View */
              <div className="space-y-4">
                {filteredResources.map((resource) => (
                  <div key={resource.id} className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:shadow-md transition-all">
                    <img
                      src={resource.thumbnail}
                      alt={resource.title}
                      className="w-24 h-16 object-cover rounded flex-shrink-0"
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-gray-900">{resource.title}</h3>
                          <span className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                            resource.type === 'video' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {getTypeIcon(resource.type)}
                            {resource.type}
                          </span>
                          {resource.isPremium && <Trophy className="w-4 h-4 text-yellow-500" />}
                        </div>
                        <button className={`p-1 rounded ${resource.isBookmarked ? 'text-blue-600' : 'text-gray-400'}`}>
                          <Bookmark className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{resource.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <div className="flex items-center gap-1">
                          <span>{resource.author.avatar}</span>
                          <span>{resource.author.name}</span>
                          {resource.author.verified && <Award className="w-3 h-3 text-blue-500" />}
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500" />
                          <span>{resource.rating}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Download className="w-4 h-4" />
                          <span>{resource.downloads.toLocaleString()}</span>
                        </div>
                        <span className="capitalize">{resource.difficulty}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {resource.tags.slice(0, 4).map((tag) => (
                            <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                            <Share2 className="w-4 h-4" />
                          </button>
                          <button className="flex items-center gap-2 px-3 py-1.5 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 text-sm">
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'collections' && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {collections.map((collection) => (
                <div key={collection.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all">
                  <img
                    src={collection.thumbnail}
                    alt={collection.name}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">{collection.name}</h3>
                      <span className={`px-2 py-1 rounded text-xs ${
                        collection.isPublic ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {collection.isPublic ? 'Public' : 'Private'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{collection.description}</p>
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                      <span>{collection.resourceCount} resources</span>
                      <span>Updated {new Date(collection.lastUpdated).toLocaleDateString()}</span>
                    </div>
                    <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700">
                      <Folder className="w-4 h-4" />
                      Open Collection
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'uploaded' && (
          <div className="p-6">
            <div className="text-center py-12">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No uploads yet</h3>
              <p className="text-gray-600 mb-6">Share your knowledge by uploading resources to help others learn.</p>
              <button className="flex items-center gap-2 px-6 py-3 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 mx-auto">
                <Upload className="w-4 h-4" />
                Upload Your First Resource
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}